import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Trash2, Copy, Tag, Percent, DollarSign, Calendar, Users } from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

const getAuthConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

const AdminPromoCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    code: "",
    discount_type: "percentage",
    discount_value: "",
    duration: "once",
    duration_months: "",
    billing_cycle: "both",
    max_uses: "",
    expires_at: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchCodes(); }, []);

  const fetchCodes = async () => {
    try {
      const res = await axios.get(`${API}/admin/promo-codes`, getAuthConfig());
      setCodes(res.data);
    } catch { toast.error("Failed to load promo codes"); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.code || !form.discount_value) {
      toast.error("Code and discount value are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/promo-codes`, {
        code: form.code,
        discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value),
        duration: form.duration,
        duration_months: form.duration === "repeating" && form.duration_months ? parseInt(form.duration_months) : null,
        billing_cycle: form.billing_cycle,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at || null,
        description: form.description,
        applicable_plans: [],
      }, getAuthConfig());
      toast.success("Promo code created & synced with Stripe");
      setShowCreate(false);
      setForm({ code: "", discount_type: "percentage", discount_value: "", duration: "once", duration_months: "", billing_cycle: "both", max_uses: "", expires_at: "", description: "" });
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create promo code");
    } finally { setSaving(false); }
  };

  const toggleActive = async (promo) => {
    try {
      await axios.put(`${API}/admin/promo-codes/${promo.promo_id}`, {
        is_active: !promo.is_active
      }, getAuthConfig());
      fetchCodes();
      toast.success(promo.is_active ? "Promo code deactivated" : "Promo code activated");
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async (promo) => {
    if (!window.confirm(`Delete promo code "${promo.code}"?`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${promo.promo_id}`, getAuthConfig());
      toast.success("Promo code deleted");
      fetchCodes();
    } catch { toast.error("Failed to delete"); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied to clipboard");
  };

  if (loading) return <div className="text-muted-foreground p-8">Loading promo codes...</div>;

  return (
    <div className="space-y-6" data-testid="admin-promo-codes">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground font-['Manrope']">Promo Codes</h2>
          <p className="text-muted-foreground text-sm">Create and manage promotion codes for checkout discounts</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="create-promo-btn">
          <Plus className="w-4 h-4 mr-2" /> New Promo Code
        </Button>
      </div>

      {codes.length === 0 ? (
        <Card className="glass-card border-theme">
          <CardContent className="p-12 text-center">
            <Tag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-foreground text-lg font-semibold mb-2">No Promo Codes Yet</h3>
            <p className="text-muted-foreground mb-4">Create your first promotion code to offer discounts</p>
            <Button onClick={() => setShowCreate(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" /> Create Promo Code
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {codes.map((promo) => (
            <Card key={promo.promo_id} className="glass-card border-theme" data-testid={`promo-card-${promo.code}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#FFCC00]/10 flex items-center justify-center">
                      {promo.discount_type === "percentage"
                        ? <Percent className="w-6 h-6 text-[#FFCC00]" />
                        : <DollarSign className="w-6 h-6 text-[#FFCC00]" />
                      }
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-foreground font-bold font-mono text-lg tracking-wider">{promo.code}</span>
                        <button onClick={() => copyCode(promo.code)} className="text-muted-foreground hover:text-foreground">
                          <Copy className="w-4 h-4" />
                        </button>
                        <Badge className={promo.is_active ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                          {promo.is_active ? "Active" : "Inactive"}
                        </Badge>
                        <Badge className={
                          (promo.billing_cycle || "both") === "annual"
                            ? "bg-purple-500/20 text-purple-400"
                            : (promo.billing_cycle || "both") === "monthly"
                              ? "bg-blue-500/20 text-blue-400"
                              : "bg-slate-500/20 text-slate-300"
                        } data-testid={`promo-cycle-badge-${promo.code}`}>
                          {(promo.billing_cycle || "both") === "both" ? "Monthly + Annual" : (promo.billing_cycle === "annual" ? "Annual only" : "Monthly only")}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground text-sm mt-1">
                        {promo.discount_type === "percentage"
                          ? `${promo.discount_value}% off`
                          : `$${promo.discount_value} off`
                        }
                        {promo.duration === "once" && " (one-time)"}
                        {promo.duration === "repeating" && ` for ${promo.duration_months} month${promo.duration_months > 1 ? "s" : ""}`}
                        {promo.duration === "forever" && " (forever)"}
                        {promo.description && ` — ${promo.description}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="text-foreground font-semibold">{promo.times_used || 0}{promo.max_uses ? `/${promo.max_uses}` : ""}</div>
                      <div className="text-muted-foreground text-xs">Uses</div>
                    </div>
                    {promo.expires_at && (
                      <div className="text-center">
                        <div className="text-foreground text-sm">{new Date(promo.expires_at).toLocaleDateString()}</div>
                        <div className="text-muted-foreground text-xs">Expires</div>
                      </div>
                    )}
                    <Switch checked={promo.is_active} onCheckedChange={() => toggleActive(promo)} />
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(promo)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-background border-theme" data-testid="create-promo-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground font-['Manrope']">Create Promo Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground">Code</Label>
              <Input
                placeholder="e.g. SUMMER25"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="input-glass font-mono uppercase"
                data-testid="promo-code-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Discount Type</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger className="input-glass" data-testid="promo-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Discount Value</Label>
                <Input
                  type="number"
                  placeholder={form.discount_type === "percentage" ? "e.g. 25" : "e.g. 10.00"}
                  value={form.discount_value}
                  onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                  className="input-glass"
                  data-testid="promo-value-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Duration</Label>
                <Select value={form.duration} onValueChange={(v) => setForm({ ...form, duration: v, duration_months: "" })}>
                  <SelectTrigger className="input-glass" data-testid="promo-duration-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">One-time</SelectItem>
                    <SelectItem value="repeating">Multiple Months</SelectItem>
                    <SelectItem value="forever">Forever</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.duration === "repeating" && (
                <div className="space-y-2">
                  <Label className="text-foreground">Number of Months</Label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="e.g. 3"
                    value={form.duration_months}
                    onChange={(e) => setForm({ ...form, duration_months: e.target.value })}
                    className="input-glass"
                    data-testid="promo-duration-months-input"
                  />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Applies To Billing Cycle</Label>
              <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
                <SelectTrigger className="input-glass" data-testid="promo-billing-cycle-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Monthly + Annual</SelectItem>
                  <SelectItem value="monthly">Monthly only</SelectItem>
                  <SelectItem value="annual">Annual only</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">The code will be rejected at checkout for billing cycles it doesn't apply to.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Max Uses (optional)</Label>
                <Input
                  type="number"
                  placeholder="Unlimited"
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  className="input-glass"
                  data-testid="promo-max-uses-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Expires (optional)</Label>
                <Input
                  type="date"
                  value={form.expires_at}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  className="input-glass"
                  data-testid="promo-expires-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Description (optional)</Label>
              <Input
                placeholder="e.g. Summer sale discount"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="input-glass"
                data-testid="promo-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-theme">Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="btn-primary" data-testid="promo-save-btn">
              {saving ? "Creating..." : "Create Code"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPromoCodes;
